import * as Sentry from '@sentry/react';
import { AllowedReducer, Constants } from '@leverege/api-redux';
import I18N from '@leverege/i18n';
import Molten from '@leverege/molten';
import { Context } from '@leverege/plugin';
import { Toast } from '@leverege/ui-elements';
import { GlobalState } from '@leverege/ui-redux';
import { canSendTagRingCommand } from "./Util.js";
import { amplitudeRingTagTrackEvent, getRingTagEventSource } from "../../tracking/AmplitudeRingTagTrackingEvent.js";
const {
  t,
  tIcon
} = I18N.ns('recovr.details.findTag.FindTagAction');
const ACTION = 'ringTag';
export default {
  id: 'recovr.findTag.FindTagAction',
  name: 'Find Tag',
  matches: [{
    client: ['DataCardDetail'],
    use: 'actionBar',
    objectType: 'tag'
  }],
  isPrepared: context => {
    const targets = Context.getTargetsOfType(context, 'tag');
    if (targets == null || targets.length === 0 || targets.length > 1) {
      return true;
    }
    const tag = GlobalState.get().models.lookup?.[targets[0].id]; // We have to pull the item from state, given that in the case of the selection, the item remains stale.

    const orgId = tag.data?.organization?.id;
    const companyId = tag.data?.company?.id;
    const locationId = tag.data?.location?.id;

    // It isn't an actual resourceId, but something closer to a ref.
    // Permissioned role will always be set at the company or location level.
    const resourceId = [orgId, companyId, locationId].filter(x => x != null).join('/');
    if (resourceId.length === 0) {
      return true;
    }
    const a = AllowedReducer.get(GlobalState.get(), resourceId);
    return a[ACTION] != null;
  },
  prepare: async context => {
    const targets = Context.getTargetsOfType(context, 'tag');
    const tag = GlobalState.get().models.lookup?.[targets[0].id]; // We have to pull the item from state, given that in the case of the selection, the item remains stale.

    const tagId = tag.id;
    const orgId = tag.data?.organization?.id;
    const companyId = tag.data?.company?.id;
    const locationId = tag.data?.location?.id;

    // It isn't an actual resourceId, but something closer to a ref.
    // Permissioned role will always be set at the company or location level.
    const resourceId = [orgId, companyId, locationId].filter(x => x != null).join('/');
    const a = AllowedReducer.get(GlobalState.get(), resourceId);
    if (a[ACTION] != null) {
      return a[ACTION];
    }
    return GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      let nApi;
      if (orgId && companyId && locationId) {
        // Almost certainly won't need to check org level with missing company/location
        nApi = api.interface(process.env.SYSTEM_ID, 'organization').obj(orgId).grp('companies').obj(companyId).grp('locations').obj(locationId).grp('tags') // Has to be kept, interface service route only exists for the tag blueprint type.
        .obj(tagId);
      } else if (companyId && locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('tags') // Has to be kept, interface service route only exists for the tag blueprint type.
        .obj(tagId);
      } else if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('tags') // Has to be kept, interface service route only exists for the tag blueprint type.
        .obj(tagId);
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('tags') // Has to be kept, interface service route only exists for the tag blueprint type.
        .obj(tagId);
      }
      const res = await nApi.srv('kudelskidevice').post(`device/${tagId}/command`, null, {
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId,
        action: ACTION,
        allowed: res.allowed === true
      });
      return res.allowed;
    });
  },
  handles: context => {
    const targets = Context.getTargetsOfType(context, 'tag');
    if (targets == null || targets.length === 0 || targets.length > 1) {
      return false;
    }
    const tag = GlobalState.get().models.lookup?.[targets[0].id]; // We have to pull the item from state, given that in the case of the selection, the item remains stale.

    const orgId = tag.data?.organization?.id;
    const companyId = tag.data?.company?.id;
    const locationId = tag.data?.location?.id;

    // It isn't an actual resourceId, but something closer to a ref.
    // Permissioned role will always be set at the company or location level.
    const resourceId = [orgId, companyId, locationId].filter(x => x != null).join('/');
    if (resourceId.length === 0) {
      return false;
    }
    const a = AllowedReducer.get(GlobalState.get(), resourceId);
    return a[ACTION] === true && canSendTagRingCommand(tag);
  },
  appearance: ({
    context
  }) => {
    const targets = Context.getTargetsOfType(context, 'tag');
    const tag = GlobalState.get().models.lookup?.[targets[0].id]; // We have to pull the item from state, given that in the case of the selection, the item remains stale.

    return {
      disabled: !canSendTagRingCommand(tag),
      name: t('name'),
      icon: tIcon('icon')
    };
  },
  perform: ({
    context
  }) => {
    console.log(context, 'CONTEXT');
    const onCommand = context.onCommand;
    const targets = Context.getTargetsOfType(context, 'tag');
    const tag = GlobalState.get().models.lookup?.[targets[0].id]; // We have to pull the item from state, given that in the case of the selection, the item remains stale.

    GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      try {
        const systemId = Molten.getConfig('api/systemId');
        const tagId = tag.id;
        const orgId = tag.data?.organization?.id;
        const companyId = tag.data?.company?.id;
        const locationId = tag.data?.location?.id;
        let nApi;
        if (orgId && companyId && locationId) {
          nApi = api.interface(systemId, 'organization').obj(orgId).grp('companies').obj(companyId).grp('locations').obj(locationId).grp('tags').obj(`${tagId}`);
        } else if (companyId && locationId) {
          nApi = api.interface(systemId, 'company').obj(companyId).grp('locations').obj(locationId).grp('tags').obj(`${tagId}`);
        } else if (companyId) {
          nApi = api.interface(systemId, 'company').obj(companyId).grp('tags').obj(tagId);
        } else {
          nApi = api.interface(systemId, 'location').obj(locationId).grp('tags').obj(tagId);
        }
        await nApi.srv('kudelskidevice').post(`device/${tagId}/command`, {
          command: {
            type: 'peripheralDevice.request.ring'
          }
        }, {});

        // Initiate tag device data subscription to correctly mirror the state of the tag
        // in the UI without waiting for periodic refreshes.
        onCommand();

        /** AMPLITUDE TRACKING CODE */
        const eventSource = getRingTagEventSource(context.clientProps?.client);
        amplitudeRingTagTrackEvent(eventSource, 'ring');
        /** END OF AMPLITUDE TRACKING CODE */

        Toast.success(t('success'));
      } catch (err) {
        Sentry.captureException(err);
        console.error(err);
        Toast.error([t('error'), err?.message]);
      }
    });
  }
};